/**
 * TeamMemberCard Component
 *
 * Displays a team member with avatar, role, and actions.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { User, Crown, Shield, MoreVertical, Mail, Clock, Trash2 } from 'lucide-react-native';
import Animated, { FadeIn, Layout } from 'react-native-reanimated';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import type { TeamMember, TeamRole } from '@/lib/team';
import { ROLE_NAMES, formatMemberSince, canManageTeam } from '@/lib/team';

// ============================================================================
// Types
// ============================================================================

interface TeamMemberCardProps {
  member: TeamMember;
  currentUserRole: TeamRole;
  onRemove?: (memberId: string) => void;
  onChangeRole?: (memberId: string, newRole: TeamRole) => void;
  index?: number;
}

// ============================================================================
// Component
// ============================================================================

export function TeamMemberCard({
  member,
  currentUserRole,
  onRemove,
  onChangeRole,
  index = 0,
}: TeamMemberCardProps) {
  const colors = useThemeColors();

  const canRemove =
    currentUserRole === 'owner'
      ? member.role !== 'owner'
      : currentUserRole === 'admin'
      ? member.role === 'member'
      : false;

  const getRoleIcon = () => {
    switch (member.role) {
      case 'owner':
        return <Crown size={14} color={colors.warning} />;
      case 'admin':
        return <Shield size={14} color={colors.primary[500]} />;
      default:
        return null;
    }
  };

  const getRoleBadgeStyle = () => {
    switch (member.role) {
      case 'owner':
        return { backgroundColor: colors.traffic.amberLight, borderColor: colors.warning };
      case 'admin':
        return { backgroundColor: colors.primary[50], borderColor: colors.primary[300] };
      default:
        return { backgroundColor: colors.surface.secondary, borderColor: colors.border.light };
    }
  };

  return (
    <Animated.View
      entering={FadeIn.delay(index * 50).duration(300)}
      layout={Layout.springify()}
    >
      <View
        style={[
          styles.container,
          {
            backgroundColor: colors.surface.primary,
            borderColor: colors.border.light,
          },
        ]}
      >
        {/* Avatar */}
        <View
          style={[
            styles.avatar,
            { backgroundColor: colors.primary[100] },
          ]}
        >
          {member.avatar ? (
            <Text style={styles.avatarText}>
              {member.name.substring(0, 2).toUpperCase()}
            </Text>
          ) : (
            <User size={20} color={colors.primary[600]} />
          )}
        </View>

        {/* Info */}
        <View style={styles.info}>
          <View style={styles.nameRow}>
            <Text
              style={[styles.name, { color: colors.text.primary }]}
              numberOfLines={1}
            >
              {member.name}
            </Text>
            {member.role === 'owner' && (
              <View style={[styles.roleBadge, getRoleBadgeStyle()]}>
                {getRoleIcon()}
                <Text style={[styles.roleText, { color: colors.warning }]}>
                  Owner
                </Text>
              </View>
            )}
            {member.role === 'admin' && (
              <View style={[styles.roleBadge, getRoleBadgeStyle()]}>
                {getRoleIcon()}
                <Text style={[styles.roleText, { color: colors.primary[600] }]}>
                  Admin
                </Text>
              </View>
            )}
          </View>
          <View style={styles.metaRow}>
            <Mail size={12} color={colors.text.tertiary} />
            <Text
              style={[styles.email, { color: colors.text.secondary }]}
              numberOfLines={1}
            >
              {member.email}
            </Text>
          </View>
          {member.joinedAt && (
            <View style={styles.metaRow}>
              <Clock size={12} color={colors.text.tertiary} />
              <Text style={[styles.meta, { color: colors.text.tertiary }]}>
                Joined {formatMemberSince(member.joinedAt)}
              </Text>
            </View>
          )}
        </View>

        {/* Actions */}
        {canRemove && onRemove && (
          <AnimatedPressable
            onPress={() => onRemove(member.id)}
            style={styles.actionButton}
          >
            <Trash2 size={18} color={colors.error} />
          </AnimatedPressable>
        )}
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  info: {
    flex: 1,
    gap: 4,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
  },
  roleBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
  },
  roleText: {
    fontSize: 11,
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  email: {
    fontSize: 13,
    flex: 1,
  },
  meta: {
    fontSize: 12,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
